import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Languages } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatLocalizedDate } from '../../utils/formatDate';

// Safe image resolver:
//  - absolute URLs (http/https) or data: URIs → use as-is
//  - bare file names (e.g. "article-1.jpg") → try require() from /assets; silently fall back to placeholder
const FALLBACK =
  'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=60';

const resolveImage = (image) => {
  if (!image) return FALLBACK;
  // Only allow absolute URLs or data URIs. Bare filenames are no longer resolved
  // via webpack require (legacy assets) to avoid dynamic-require lint errors.
  if (/^(https?:|data:|\/)/i.test(image)) return image;
  return FALLBACK;
};

const CTA_LABEL = {
  en: 'Read full article',
  pt: 'Ler artigo completo',
  fr: 'Lire l\'article',
  ar: 'قراءة المقال كاملًا',
  zh: '阅读完整文章',
};

const AUTO_LABEL = {
  en: 'Auto-translated',
  pt: 'Tradução automática',
  fr: 'Traduit automatiquement',
  ar: 'مترجم تلقائيًا',
  zh: '自动翻译',
};

const ArticleCard = ({ category, title, excerpt, image, date, link, ctaLabel, _auto_translated: autoTranslated }) => {
  const { language } = useLanguage();
  const src = resolveImage(image);
  const href = link || '/insights';
  const isExternal = typeof href === 'string' && /^https?:/i.test(href);
  const ctaText = ctaLabel || CTA_LABEL[language] || CTA_LABEL.en;
  const autoLabel = AUTO_LABEL[language] || AUTO_LABEL.en;

  const titleEl = (
    <h4 className="text-2xl font-semibold text-[#2B2D42] mb-2 line-clamp-2 group-hover:text-[#00B4D8] transition-colors">
      {title}
    </h4>
  );

  const ctaButton = (
    <button
      type="button"
      data-testid="article-cta-btn"
      className="mt-2 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-gradient-to-r from-[#0F172A] to-[#1E293B] text-white text-sm font-bold tracking-wide hover:from-[#FF6B35] hover:to-[#FF8F4A] transition-all"
    >
      {ctaText}
      <ArrowRight className="w-3.5 h-3.5" />
    </button>
  );

  return (
    <div className="bg-white border border-[#E9ECEF] rounded-xl overflow-hidden hover:border-[#00B4D8] hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group">
      <div className="relative overflow-hidden aspect-video bg-[#F1F3F5]">
        <img
          src={src}
          alt={title}
          loading="lazy"
          className="w-full h-full object-cover"
          onError={(e) => { if (e.currentTarget.src !== FALLBACK) e.currentTarget.src = FALLBACK; }}
        />
      </div>
      <div className="p-6">
        {autoTranslated && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-semibold uppercase tracking-wider rounded mb-2"
            title={autoLabel}
            data-testid="article-auto-translated-badge"
          >
            <Languages className="w-3 h-3" /> {autoLabel}
          </span>
        )}
        {category && (
          <span className="inline-block px-3 py-1.5 bg-[#00B4D8]/10 text-[#00B4D8] text-xs font-semibold uppercase rounded mb-3">
            {category}
          </span>
        )}
        {isExternal ? (
          <a href={href} target="_blank" rel="noreferrer">{titleEl}</a>
        ) : (
          <Link to={href}>{titleEl}</Link>
        )}
        <p className="text-base text-[#6C757D] leading-relaxed mb-4 line-clamp-3">
          {excerpt}
        </p>
        <div className="text-xs text-[#ADB5BD] mb-3">{formatLocalizedDate(date, language)}</div>
        {isExternal ? (
          <a href={href} target="_blank" rel="noreferrer">{ctaButton}</a>
        ) : (
          <Link to={href}>{ctaButton}</Link>
        )}
      </div>
    </div>
  );
};

export default ArticleCard;
